import { supabase } from '../lib/supabase';
import {
  getGoogleClientId,
  getGoogleRedirectUri,
  getMicrosoftClientId,
  getMicrosoftRedirectUri,
} from './oauthConfigService';
import { GOOGLE_UNIFIED_SCOPES } from './googleScopes';
import type {
  CalendarConnection,
  CalendarProvider,
  AvailabilitySettings,
  OAuthCallbackData,
  AvailabilityRequest,
  AvailabilityResponse
} from '../types/calendar';

const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const MICROSOFT_OAUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const MICROSOFT_SCOPES = 'Calendars.ReadWrite User.Read offline_access';

export const calendarService = {
  initiateGoogleOAuth: async (clientId: string, userId: string): Promise<string> => {
    const googleClientId = await getGoogleClientId();
    const redirectUri = await getGoogleRedirectUri('calendar');

    if (!googleClientId || !redirectUri) {
      throw new Error('Google Calendar OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId });

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: GOOGLE_UNIFIED_SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state,
    });

    return `${GOOGLE_OAUTH_URL}?${params.toString()}`;
  },

  initiateMicrosoftOAuth: async (clientId: string, userId: string): Promise<string> => {
    const microsoftClientId = await getMicrosoftClientId();
    const redirectUri = await getMicrosoftRedirectUri('calendar');

    if (!microsoftClientId || !redirectUri) {
      throw new Error('Microsoft Calendar OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId });

    const params = new URLSearchParams({
      client_id: microsoftClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: MICROSOFT_SCOPES,
      response_mode: 'query',
      prompt: 'consent',
      state,
    });

    return `${MICROSOFT_OAUTH_URL}?${params.toString()}`;
  },

  handleMicrosoftOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string,
    salesMemberId?: string
  ): Promise<OAuthCallbackData> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getMicrosoftRedirectUri('calendar');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/microsoft-calendar-oauth-callback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            code,
            client_id: clientId,
            user_id: userId,
            redirect_uri: redirectUri,
            ...(salesMemberId ? { sales_member_id: salesMemberId } : {}),
          }),
        }
      );

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Microsoft calendar' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      console.error('Error handling Microsoft OAuth callback:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  handleOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string,
    salesMemberId?: string
  ): Promise<OAuthCallbackData> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getGoogleRedirectUri('calendar');

      console.log('=== OAUTH CALLBACK START ===');
      console.log('handleOAuthCallback - supabaseUrl:', supabaseUrl);
      console.log('handleOAuthCallback - redirectUri:', redirectUri);
      console.log('handleOAuthCallback - clientId:', clientId);
      console.log('handleOAuthCallback - userId:', userId);
      console.log('handleOAuthCallback - salesMemberId:', salesMemberId);
      console.log('handleOAuthCallback - code (first 20 chars):', code?.substring(0, 20));

      const requestUrl = `${supabaseUrl}/functions/v1/google-oauth-callback`;
      console.log('Making request to:', requestUrl);

      const requestBody = {
        code,
        client_id: clientId,
        user_id: userId,
        redirect_uri: redirectUri,
        ...(salesMemberId ? { sales_member_id: salesMemberId } : {}),
      };
      console.log('Request body:', JSON.stringify(requestBody, null, 2));

      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('OAuth callback response status:', response.status);
      console.log('OAuth callback response headers:', Object.fromEntries(response.headers.entries()));

      const responseText = await response.text();
      console.log('OAuth callback response text:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('OAuth callback parsed data:', data);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        return {
          success: false,
          error: 'Invalid response from server',
        };
      }

      if (!response.ok || !data.success) {
        console.error('OAuth callback failed:', data.error);
        console.error('Full error response:', data);
        return {
          success: false,
          error: data.error || 'Failed to connect calendar',
        };
      }

      console.log('=== OAUTH CALLBACK SUCCESS ===');
      return {
        success: true,
        connection: data.connection,
      };
    } catch (error) {
      console.error('=== OAUTH CALLBACK ERROR ===');
      console.error('Error handling OAuth callback:', error);
      console.error('Error type:', typeof error);
      console.error('Error stack:', error instanceof Error ? error.stack : 'N/A');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getCalendarConnection: async (clientId: string, userId?: string): Promise<CalendarConnection | null> => {
    try {
      let query = supabase
        .from('calendar_connections')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('Error fetching calendar connection:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting calendar connection:', error);
      return null;
    }
  },

  disconnectCalendar: async (clientId: string, provider: CalendarProvider = 'google'): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const endpoint = provider === 'microsoft'
        ? 'microsoft-calendar-disconnect'
        : 'disconnect-calendar';

      const response = await fetch(
        `${supabaseUrl}/functions/v1/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify({ client_id: clientId }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || 'Failed to disconnect calendar',
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getAvailabilitySettings: async (clientId: string): Promise<AvailabilitySettings | null> => {
    try {
      const { data, error } = await supabase
        .from('availability_settings')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching availability settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting availability settings:', error);
      return null;
    }
  },

  saveAvailabilitySettings: async (
    settings: Partial<AvailabilitySettings>
  ): Promise<{ success: boolean; error?: string; data?: AvailabilitySettings }> => {
    try {
      const existing = await calendarService.getAvailabilitySettings(settings.client_id!);

      if (existing) {
        const { data, error } = await supabase
          .from('availability_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating availability settings:', error);
          return { success: false, error: error.message };
        }

        return { success: true, data };
      } else {
        const { data, error } = await supabase
          .from('availability_settings')
          .insert({
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating availability settings:', error);
          return { success: false, error: error.message };
        }

        return { success: true, data };
      }
    } catch (error) {
      console.error('Error saving availability settings:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getCalendarAvailability: async (
    request: AvailabilityRequest
  ): Promise<AvailabilityResponse> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-calendar-availability`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify(request),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || 'Failed to fetch availability',
        };
      }

      return {
        success: true,
        slots: data.slots,
      };
    } catch (error) {
      console.error('Error getting calendar availability:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getSalesTeamMembers: async (clientId: string) => {
    const { data, error } = await supabase
      .from('sales_team_members')
      .select('*')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching sales team members:', error);
      return [];
    }
    return data || [];
  },

  addSalesTeamMember: async (member: {
    client_id: string;
    name: string;
    email: string;
    markets?: any[];
  }) => {
    const { data, error } = await supabase
      .from('sales_team_members')
      .insert({
        ...member,
        markets: member.markets || [],
      })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  updateSalesTeamMember: async (memberId: string, updates: Record<string, any>) => {
    const { data, error } = await supabase
      .from('sales_team_members')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', memberId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  removeSalesTeamMember: async (memberId: string) => {
    const { error } = await supabase
      .from('sales_team_members')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', memberId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  getAvailabilitySettingsForMember: async (clientId: string, salesMemberId: string): Promise<AvailabilitySettings | null> => {
    try {
      const { data, error } = await supabase
        .from('availability_settings')
        .select('*')
        .eq('client_id', clientId)
        .eq('sales_member_id', salesMemberId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching member availability settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting member availability settings:', error);
      return null;
    }
  },

  saveAvailabilitySettingsForMember: async (
    settings: Partial<AvailabilitySettings>,
    salesMemberId: string
  ): Promise<{ success: boolean; error?: string; data?: AvailabilitySettings }> => {
    try {
      const existing = await calendarService.getAvailabilitySettingsForMember(settings.client_id!, salesMemberId);

      const payload = { ...settings, sales_member_id: salesMemberId };

      if (existing) {
        const { data, error } = await supabase
          .from('availability_settings')
          .update({
            ...payload,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating member availability settings:', error);
          return { success: false, error: error.message };
        }

        return { success: true, data };
      } else {
        const { data, error } = await supabase
          .from('availability_settings')
          .insert({
            ...payload,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating member availability settings:', error);
          return { success: false, error: error.message };
        }

        return { success: true, data };
      }
    } catch (error) {
      console.error('Error saving member availability settings:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getCalendarConnectionForMember: async (clientId: string, salesMemberId: string) => {
    const { data, error } = await supabase
      .from('calendar_connections')
      .select('*')
      .eq('client_id', clientId)
      .eq('sales_member_id', salesMemberId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) return null;
    return data;
  },

  disconnectMemberCalendar: async (clientId: string, salesMemberId: string, provider: CalendarProvider) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const endpoint = provider === 'microsoft'
      ? 'microsoft-calendar-disconnect'
      : 'disconnect-calendar';

    const response = await fetch(
      `${supabaseUrl}/functions/v1/${endpoint}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ client_id: clientId, sales_member_id: salesMemberId }),
      }
    );

    const data = await response.json();
    return data;
  },

  initiateGoogleOAuthForMember: async (clientId: string, userId: string, salesMemberId: string): Promise<string> => {
    const googleClientId = await getGoogleClientId();
    const redirectUri = await getGoogleRedirectUri('calendar');

    const state = JSON.stringify({ client_id: clientId, user_id: userId, sales_member_id: salesMemberId });

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: GOOGLE_UNIFIED_SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state,
    });

    return `${GOOGLE_OAUTH_URL}?${params.toString()}`;
  },

  initiateMicrosoftOAuthForMember: async (clientId: string, userId: string, salesMemberId: string): Promise<string> => {
    const msClientId = await getMicrosoftClientId();
    const redirectUri = await getMicrosoftRedirectUri('calendar');

    const state = JSON.stringify({ client_id: clientId, user_id: userId, sales_member_id: salesMemberId });

    const params = new URLSearchParams({
      client_id: msClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: MICROSOFT_SCOPES,
      state,
    });

    return `${MICROSOFT_OAUTH_URL}?${params.toString()}`;
  },

  getMyLinkedSalesMember: async (clientId: string, authUserId: string) => {
    const { data, error } = await supabase
      .from('sales_team_members')
      .select('*')
      .eq('client_id', clientId)
      .eq('linked_user_id', authUserId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching linked sales member:', error);
      return null;
    }
    return data;
  },

  getLinkedPlatformMembers: async (clientId: string) => {
    const { data, error } = await supabase
      .from('sales_team_members')
      .select('*')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .not('linked_user_id', 'is', null)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching linked platform members:', error);
      return [];
    }
    return data || [];
  },

  toggleMemberAdminPool: async (memberId: string, inAdminPool: boolean) => {
    const { error } = await supabase
      .from('sales_team_members')
      .update({ in_admin_pool: inAdminPool, updated_at: new Date().toISOString() })
      .eq('id', memberId);

    if (error) {
      console.error('Error toggling admin pool:', error);
      return false;
    }
    return true;
  },
};
